import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Seo } from '../components/Seo';
import { LocalizedLink } from '../components/LocalizedLink';
import { ProductCard } from '../components/ProductCard';
import { categories, getCategory, productsByCategory } from '../data/products';

export function ProductCategory() {
  const { t } = useTranslation();
  const { category: categorySlug = '' } = useParams();
  const category = getCategory(categorySlug);
  const models = productsByCategory(categorySlug);

  if (!category) {
    return (
      <section className="section">
        <h1 className="text-3xl font-black">{t('products:notFound.title')}</h1>
        <LocalizedLink className="btn-primary mt-6" to="/products">
          {t('products:notFound.back')}
        </LocalizedLink>
      </section>
    );
  }

  const related = categories.filter((c) => c.slug !== categorySlug);
  const localizedTitle = t(`products:categories.${categorySlug}.title`);
  const localizedTagline = t(`products:categories.${categorySlug}.tagline`);
  const localizedDescription = t(`products:categories.${categorySlug}.description`);

  return (
    <>
      <Seo
        title={`${localizedTitle} | GyroNavi`}
        description={localizedDescription}
        path={`/products/${categorySlug}`}
      />
      <section className="bg-ink text-white">
        <div className="section py-14">
          <nav className="mb-4 text-sm text-slate-400">
            <LocalizedLink to="/" className="hover:text-white">{t('products:breadcrumb.home')}</LocalizedLink> ›{' '}
            <LocalizedLink to="/products" className="hover:text-white">{t('products:breadcrumb.products')}</LocalizedLink> ›{' '}
            <span>{localizedTitle}</span>
          </nav>
          <h1 className="text-4xl font-black">{localizedTitle}</h1>
          <p className="mt-4 text-lg font-semibold text-aqua">{localizedTagline}</p>
          <p className="mt-4 max-w-3xl leading-8 text-slate-300">{localizedDescription}</p>
        </div>
      </section>

      <section className="section">
        <div className="mb-6 text-center">
          <h2 className="text-3xl font-black text-ink">{t('products:models.title')}</h2>
          <p className="mt-2 text-slate-600">{t('products:models.subtitle')}</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {models.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {related.length ? (
        <section className="bg-slate-100">
          <div className="section">
            <div className="mb-6 text-center">
              <h2 className="text-3xl font-black text-ink">{t('products:related.title')}</h2>
              <p className="mt-2 text-slate-600">{t('products:related.subtitle')}</p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((c) => (
                <LocalizedLink key={c.slug} to={`/products/${c.slug}`} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-panel">
                  <div className="flex h-32 items-center justify-center bg-slate-50 p-3">
                    <img src={c.image} alt={t(`products:categories.${c.slug}.title`)} loading="lazy" className="max-h-full object-contain" />
                  </div>
                  <h3 className="mt-4 font-bold text-ink">{t(`products:categories.${c.slug}.title`)}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{t(`products:categories.${c.slug}.tagline`)}</p>
                </LocalizedLink>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
